<script lang="ts">
  import { onMount } from 'svelte'
  import type { Editor } from '@tiptap/core'
  import { Link2, Check, X, Trash2 } from 'lucide-svelte'
  import { Decoration, DecorationSet } from '@tiptap/pm/view'
  import { Plugin, PluginKey } from '@tiptap/pm/state'
  
  interface Props {
    editor: Editor
    onClose: () => void
  }
  
  let { editor, onClose }: Props = $props()
  
  let url = $state('')
  let inputElement: HTMLInputElement
  let containerElement: HTMLDivElement
  
  // Store the current selection
  const { from, to } = editor.state.selection
  const selectedText = editor.state.doc.textBetween(from, to)
  
  // Get current link if editing
  const currentLink = editor.getAttributes('link').href || ''
  const hasLink = !!currentLink
  url = currentLink
  
  // Plugin key for selection highlight
  const selectionHighlightKey = new PluginKey('linkSelectionHighlight')
  
  function handleSubmit() {
    // Remove highlight decoration
    removeHighlight()

    // Restore selection and apply link
    editor.chain()
      .focus()
      .setTextSelection({ from, to })
      .run()

    if (!url) {
      editor.chain().focus().unsetLink().run()
    } else {
      // Check if it's a YouTube URL
      const youtubeRegex = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/
      const match = url.match(youtubeRegex)

      if (match) {
        // It's a YouTube URL - insert YouTube embed instead of link
        const videoId = match[1]

        // Delete selected text (if any) and insert YouTube embed
        editor.chain()
          .focus()
          .deleteRange({ from, to })
          .setYoutubeVideo({
            src: url
          })
          .run()
      } else {
        // Regular link - add protocol if missing
        let finalUrl = url
        if (!url.match(/^https?:\/\//) && !url.match(/^mailto:/) && !url.match(/^tel:/)) {
          finalUrl = 'https://' + url
        }

        // Use TipTap's setLink command
        editor.chain().focus().setLink({ href: finalUrl }).run()
      }
    }
    onClose()
  }
  
  function handleRemove() {
    // Remove highlight decoration
    removeHighlight()
    
    // Restore selection and remove link
    editor.chain()
      .focus()
      .setTextSelection({ from, to })
      .unsetLink()
      .run()
    onClose()
  }
  
  function handleCancel() {
    // Remove highlight decoration
    removeHighlight()
    onClose()
  }
  
  function handleKeydown(e: KeyboardEvent) {
    e.stopPropagation()
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }
  
  // Handle click outside
  function handleClickOutside(e: MouseEvent) {
    if (containerElement && !containerElement.contains(e.target as Node)) {
      handleCancel()
    }
  }
  
  // Add highlight decoration
  function addHighlight() {
    if (from === to) return
    
    const tr = editor.state.tr
    const decoration = Decoration.inline(from, to, {
      class: 'link-selection-highlight'
    })
    
    tr.setMeta(selectionHighlightKey, {
      add: { from, to, decoration }
    })
    
    editor.view.dispatch(tr)
  }
  
  // Remove highlight decoration
  function removeHighlight() {
    const tr = editor.state.tr
    tr.setMeta(selectionHighlightKey, { clear: true })
    editor.view.dispatch(tr)
  }
  
  onMount(() => {
    // Create and register the plugin
    const plugin = new Plugin({
      key: selectionHighlightKey,
      state: {
        init() {
          return DecorationSet.empty
        },
        apply(tr, oldState) {
          const meta = tr.getMeta(selectionHighlightKey)
          
          if (meta?.clear) {
            return DecorationSet.empty
          }
          
          if (meta?.add) {
            const decoration = Decoration.inline(meta.add.from, meta.add.to, {
              class: 'link-selection-highlight'
            })
            return DecorationSet.create(tr.doc, [decoration])
          }
          
          return oldState
        }
      },
      props: {
        decorations(state) {
          return plugin.getState(state)
        }
      }
    })
    
    // Register the plugin
    editor.registerPlugin(plugin)
    
    // Add highlight decoration
    addHighlight()
    
    // Focus and select input after decoration
    setTimeout(() => {
      if (inputElement) {
        inputElement.focus()
        if (url) {
          inputElement.select()
        }
      }
    }, 0)
    
    // Add click outside listener
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 0)
    
    return () => {
      document.removeEventListener('click', handleClickOutside)
      // Unregister plugin and remove highlight
      removeHighlight()
      editor.unregisterPlugin(selectionHighlightKey)
    }
  })
</script>

<div 
  bind:this={containerElement}
  class="link-input-container"
>
  <div class="link-input-wrapper">
    <Link2 size={14} class="link-icon" />
    <input
      bind:this={inputElement}
      type="text"
      bind:value={url}
      placeholder="URL 입력 (예: example.com 또는 youtube.com/watch?v=...)"
      onkeydown={handleKeydown}
      class="link-input"
    />
    
    <div class="link-actions">
      {#if hasLink}
        <button
          class="link-btn link-btn-remove"
          onclick={handleRemove}
          title="링크 제거"
        >
          <Trash2 size={14} />
        </button>
      {/if}
      
      <button
        class="link-btn link-btn-cancel"
        onclick={handleCancel}
        title="취소"
      >
        <X size={14} />
      </button>
      
      <button
        class="link-btn link-btn-submit"
        onclick={handleSubmit}
        title="적용"
      >
        <Check size={14} />
      </button>
    </div>
  </div>
</div>

<style>
  .link-input-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 100000;
    animation: fade-in 0.15s ease;
  }
  
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translate(-50%, -48%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }
  
  .link-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: var(--bg-primary, white);
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 10px;
    box-shadow: 
      0 1px 3px 0 rgb(0 0 0 / 0.1),
      0 1px 2px -1px rgb(0 0 0 / 0.1),
      0 10px 15px -3px rgb(0 0 0 / 0.1);
    min-width: 360px;
  }
  
  :global(.link-icon) {
    color: var(--text-tertiary, #9ca3af);
    flex-shrink: 0;
  }
  
  .link-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: var(--text-primary, #111827);
    padding: 4px;
  }
  
  .link-input::placeholder {
    color: var(--text-tertiary, #9ca3af);
  }
  
  .link-actions {
    display: flex;
    gap: 4px;
    align-items: center;
  }
  
  .link-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    color: var(--text-secondary, #6b7280);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  
  .link-btn:hover {
    background: var(--bg-secondary, #f3f4f6);
    transform: scale(1.1);
  }
  
  .link-btn:active {
    transform: scale(0.95);
  }
  
  .link-btn-submit {
    background: var(--accent-color, #3b82f6);
    color: white;
  }
  
  .link-btn-submit:hover {
    background: var(--accent-hover, #2563eb);
  }
  
  .link-btn-remove {
    color: var(--error-color, #ef4444);
  }
  
  .link-btn-remove:hover {
    background: var(--error-bg, #fee2e2);
  }
  
  /* Dark mode */
  :global(.dark-theme) .link-input-wrapper {
    background: var(--bg-primary, #1f2937);
    border-color: var(--border-color, #374151);
  }
  
  :global(.dark-theme) .link-input {
    color: var(--text-primary, #f3f4f6);
  }
  
  :global(.dark-theme) .link-btn:hover {
    background: var(--bg-secondary, #374151);
  }
  
  /* Auto dark mode (prefers-color-scheme) */
  @media (prefers-color-scheme: dark) {
    .link-input-wrapper {
      background: var(--bg-primary, #1f2937);
      border-color: var(--border-color, #374151);
    }
    
    .link-input {
      color: var(--text-primary, #f3f4f6);
    }
    
    .link-btn:hover {
      background: var(--bg-secondary, #374151);
    }
  }
  
  /* Selection highlight style */
  :global(.link-selection-highlight) {
    background-color: rgba(99, 102, 241, 0.25) !important;
    border-radius: 2px;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }
  
  /* Dark mode selection highlight */
  :global(.dark-theme .link-selection-highlight) {
    background-color: rgba(129, 140, 248, 0.4) !important;
    outline: 1px solid rgba(129, 140, 248, 0.3);
  }
  
  @media (prefers-color-scheme: dark) {
    :global(.link-selection-highlight) {
      background-color: rgba(129, 140, 248, 0.4) !important;
      outline: 1px solid rgba(129, 140, 248, 0.3);
    }
  }
</style>